/*************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/

import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';

import SearchHostContext from '../../../utils/SearchHostContext';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import { getLocalizedURL, normalize } from '@intl/adobe-locales';

import './SearchDropDownQuickLinksItem.scss';
import { PRESEARCH_UI_SORT_ORDERS } from '../../../containers/SearchResultsPage/SearchConstants';

type OwnProps = {
	type: string;
	data: Record<string, any>;
	index: number;
	count: number;
};

type SearchDropDownQuickLinksItemProps = OwnProps & WrappedComponentProps & PropsFromRedux;

export class SearchDropDownQuickLinksItem extends ClickBase<SearchDropDownQuickLinksItemProps> {
	searchHostContext: HostContext;
	private analytics: SearchAnalytics;
	private linkItemRef: React.RefObject<HTMLDivElement>;
	constructor(props: SearchDropDownQuickLinksItemProps) {
		super(props);
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		this.onClick = this.onClick.bind(this);
		this.linkItemRef = React.createRef();
	}

	onClick(event: any): void {
		if (event.which === 13 || event.keyCode === 13 || event.type === 'click') {
			const {
				type,
				action: { url },
			} = this.props.data;
			this.linkItemRef.current && this.linkItemRef.current.blur();
			const openUrl = url
				? AnalyticsUtils.appendUrlWithAnalytics(
						getLocalizedURL(url, normalize(this.searchHostContext.locale, 'ccsearch')),
					)
				: undefined;

			// log quick links click event to ingest
			this.analytics.postPreSearchItemAction({
				recommendationType: this.props.type,
				eventType: 'click',
				uiSortOrder: PRESEARCH_UI_SORT_ORDERS.quickLinks,
				data: [Object.assign({}, this.props.data, { actionUrl: openUrl })],
				eventIndex: this.props.index,
			});

			this.onClickUrl(this.searchHostContext, type, openUrl);
			event.stopPropagation();
		}
	}

	separator(index) {
		if (index < 2) {
			return <span> &nbsp;&nbsp;| </span>;
		}
		return <span> &nbsp;&nbsp; </span>;
	}

	render() {
		return (
			<div
				className='search-dropdown-quick-links-item'
				id={`search-dropdown-quick-links-link-item-${this.props.index}`}
			>
				<div
					className='search-dropdown-quick-links-item-link'
					key={this.props.data.id}
					role='link'
					tabIndex={-1}
					onClick={this.onClick}
					ref={this.linkItemRef}
				>
					<span>{this.props.data.name}</span>
				</div>
				{this.separator(this.props.index)}
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchConfig: search.searchConfig,
	searchAppMetadataList: search.searchAppMetadataList,
});

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropDownQuickLinksItem));
